import {
  CanonicalDataType,
  isLatitudeType,
  isLongitudeType,
  LatitudeDataType,
  LongitudeDataType,
  MessagePathPart,
  MessagePathPartType,
} from "@/shared/domain/topics";
import { MapPath, TopicData } from "@/shared/state/visualization/schema/v1";

export interface TimestampedLocation {
  latitude: number;
  longitude: number;
  timestamp: number;
}

export interface GeoMessagePath {
  messagePath: MessagePathPart[];
  canonicalDataType: LatitudeDataType | LongitudeDataType;
}

function getTopicDataType(topicData: TopicData): CanonicalDataType | undefined {
  const finalMsgPathPart = [...topicData.messagePath.parts].pop();
  if (finalMsgPathPart && finalMsgPathPart.type === MessagePathPartType.Attr) {
    return finalMsgPathPart.dataType;
  }
}

function getTopicDataMsgPath(topicData: TopicData): MessagePathPart[] {
  return topicData.messagePath.parts;
}

export interface GeoPathPair {
  latMessagePath: GeoMessagePath;
  lonMessagePath: GeoMessagePath;
  latTopicData: TopicData;
  lonTopicData: TopicData;
}

/**
 * Extract latitude and longitude message paths from the path data.
 * Can't assume data[0] is always latitude because the order in path.data depends on what was dropped first.
 */
export function getGeoMessagePaths(path: MapPath): GeoPathPair {
  const [dataA, dataB] = path.data;
  const msgPathA = getTopicDataMsgPath(dataA);
  const dataTypeA = getTopicDataType(dataA);
  const msgPathB = getTopicDataMsgPath(dataB);
  const dataTypeB = getTopicDataType(dataB);

  if (!msgPathA || !dataTypeA || !msgPathB || !dataTypeB) {
    throw new Error("Missing required geographic data");
  }

  if (isLatitudeType(dataTypeA) && isLongitudeType(dataTypeB)) {
    return {
      latMessagePath: { messagePath: msgPathA, canonicalDataType: dataTypeA },
      lonMessagePath: { messagePath: msgPathB, canonicalDataType: dataTypeB },
      latTopicData: dataA,
      lonTopicData: dataB,
    };
  }

  if (isLongitudeType(dataTypeA) && isLatitudeType(dataTypeB)) {
    return {
      latMessagePath: { messagePath: msgPathB, canonicalDataType: dataTypeB },
      lonMessagePath: { messagePath: msgPathA, canonicalDataType: dataTypeA },
      latTopicData: dataB,
      lonTopicData: dataA,
    };
  }

  throw new Error(
    `Expected one latitude and one longitude type, got ${dataTypeA} and ${dataTypeB}`,
  );
}
